import {Select} from '@mantine/core';
import {ColorSwatch} from 'src/design/atoms/ColorSwatch';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {produce} from 'immer';
import React from 'react';
import {ContentTitle} from 'src/design/atoms/Typography';
import {FormField} from 'src/app/forms/FormField';
import {OPTION_COLOR_SELECT_OPTIONS} from 'src/app/forms/optionColors';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

const positiveValueField: IFormFieldV2 = {
  name: 'positiveText',
  label: 'Positive Value',
  type: 'text',
  description: 'Text to display for the checked/on state',
};

const negativeValueField: IFormFieldV2 = {
  name: 'negativeText',
  label: 'Negative Value',
  type: 'text',
  description: 'Text to display for the unchecked/off state',
};

const renderColorOption = ({option}: {option: {value: string; label: string}}) => {
  const colorOption = OPTION_COLOR_SELECT_OPTIONS.find(
    c => c.value === option.value
  );
  return (
    <Group gap="xs" wrap="nowrap">
      {colorOption && <ColorSwatch color={colorOption.swatch} size={14} />}
      <Text size="sm">{option.label}</Text>
    </Group>
  );
};

export const YesNoSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const handleTogglePostiveValueChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.switchOptions = {
            ...draft.switchOptions,
            positiveText: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleToggleNegativeValueChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.switchOptions = {
            ...draft.switchOptions,
            negativeText: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handlePositiveColorChange = React.useCallback(
    (color: string | null) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.switchOptions = {
            ...draft.switchOptions,
            positiveColor: color ?? undefined,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleNegativeColorChange = React.useCallback(
    (color: string | null) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.switchOptions = {
            ...draft.switchOptions,
            negativeColor: color ?? undefined,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  if (!field || field.type !== 'switch') return null;

  const positiveColor = field.switchOptions?.positiveColor ?? null;
  const negativeColor = field.switchOptions?.negativeColor ?? null;

  return (
    <>
      <ContentTitle text="Value Options" />
      <FormField
        field={positiveValueField}
        value={field.switchOptions?.positiveText || ''}
        onChange={handleTogglePostiveValueChange}
      />
      <FormField
        field={negativeValueField}
        value={field.switchOptions?.negativeText || ''}
        onChange={handleToggleNegativeValueChange}
      />
      <ContentTitle text="State Colors" />
      <Stack gap="xs">
        <Select
          label="Checked/On Color"
          placeholder="None"
          clearable
          value={positiveColor}
          onChange={handlePositiveColorChange}
          data={OPTION_COLOR_SELECT_OPTIONS.map(option => ({
            value: option.value,
            label: option.label,
          }))}
          renderOption={renderColorOption}
          leftSection={
            positiveColor ? (
              <ColorSwatch
                color={
                  OPTION_COLOR_SELECT_OPTIONS.find(
                    c => c.value === positiveColor
                  )?.swatch ?? 'transparent'
                }
                size={14}
              />
            ) : undefined
          }
        />
        <Select
          label="Unchecked/Off Color"
          placeholder="None"
          clearable
          value={negativeColor}
          onChange={handleNegativeColorChange}
          data={OPTION_COLOR_SELECT_OPTIONS.map(option => ({
            value: option.value,
            label: option.label,
          }))}
          renderOption={renderColorOption}
          leftSection={
            negativeColor ? (
              <ColorSwatch
                color={
                  OPTION_COLOR_SELECT_OPTIONS.find(
                    c => c.value === negativeColor
                  )?.swatch ?? 'transparent'
                }
                size={14}
              />
            ) : undefined
          }
        />
      </Stack>
    </>
  );
});
